import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, CreditCard, FolderKanban, Package, LogOut, BookOpen, Key, Server, Cpu, Zap, Route, DollarSign, Shield, BarChart3, Link, Sparkles, Video, Image, UserCircle, LayoutTemplate } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardLayout({ setIsAuthenticated }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    fetchPendingCount();
    const interval = setInterval(fetchPendingCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchPendingCount = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) return;
      const resp = await axios.get(`${BACKEND_URL}/api/users/pending-count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPendingCount(resp.data.count || 0);
    } catch (e) { /* silently ignore */ }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const navItems = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/users', label: 'Users', icon: Users },
    { path: '/transactions', label: 'Transactions', icon: CreditCard },
    { path: '/credits', label: 'Credits', icon: CreditCard },
    { path: '/projects', label: 'Projects', icon: FolderKanban },
    { path: '/assets', label: 'Assets', icon: Package },
    { path: '/api-keys', label: 'API Keys', icon: Key },
    { path: '/ai-providers', label: 'AI Providers', icon: Server },
    { path: '/ai-credentials', label: 'AI Credentials', icon: Key },
    { path: '/ai-models', label: 'AI Models', icon: Cpu },
    { path: '/capabilities', label: 'Capabilities', icon: Zap },
    { path: '/routing-rules', label: 'Routing Rules', icon: Route },
    { path: '/pricing-rules', label: 'Pricing Rules', icon: DollarSign },
    { path: '/system-controls', label: 'System Controls', icon: Shield },
    { path: '/usage-analytics', label: 'Usage Analytics', icon: BarChart3 },
    { path: '/external-apps', label: 'External Apps', icon: Link },
    { path: '/tutorials', label: 'Tutorials', icon: Video },
    { path: '/example-projects', label: 'Example Projects', icon: Image },
    { path: '/actor-assets', label: 'Actor Assets', icon: UserCircle },
    { path: '/landing-assets', label: 'Landing Page Assets', icon: LayoutTemplate },
    { path: '/documentation', label: 'Documentation', icon: BookOpen },
  ];

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  return (
    <div className="flex h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-slate-200 shadow-lg flex flex-col">
        <div className="p-6 border-b border-slate-200">
          <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
            Loopai Admin
          </h1>
          <p className="text-xs text-slate-500 mt-1">Control Panel</p>
        </div>

        <nav className="flex-1 p-4 space-y-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <button
                key={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  active
                    ? 'bg-purple-100 text-purple-700 font-semibold shadow-sm'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span>{item.label}</span>
                {item.path === '/users' && pendingCount > 0 && (
                  <span className="ml-auto bg-amber-500 text-white rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none">
                    {pendingCount}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-200">
          <Button
            onClick={handleLogout}
            data-testid="logout-button"
            variant="outline"
            className="w-full flex items-center gap-2 text-red-600 hover:bg-red-50 hover:text-red-700"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
